// External depedencies
import React from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import { Link } from 'react-router-dom';
import { useTranslation, withTranslation } from 'react-i18next';

// Internal dependencies
import { getValidActivitiesForStudents } from "../../services/API";
import { getActivityStatus, statusTooltips } from "../../services/displayStatus";
import { sortByStatus, sortActivitiesByDate } from "../../services/util";
import Loading from '../Global/Loading';
import Dropdown from '../Global/Dropdown';
import Toggle from '../Global/Toggle';
import creators from '../Reports/state/roster/creators';
import { dropdownTypes } from '../Reports/values/enums';
import {assignmentTypes} from '../../constants/constants';
import { getMostRecentEquating, setMostRecentEquating, getMostRecentShowAssessments, setMostRecentShowAssessments } from '../../services/teacher/recentTeacherSelections';
import ClassWithNoStudents from './ErrorMessages/ClassWithNoStudents';
import { educatorSupportHelpLinks } from '../../constants/helpLinks';
import TrackingReportButton from './TrackingReportButton';
import { GetBrandedReviewActivityPracticeOnly, GetBrandedHideAssessmentPrompt, GetBrandedTutorName } from '../../services/BrandingService';
import { getSchoolYear } from "Components/Reports/util/validation";

const NoActivitiesBox = () => (
  <div className="teacherNoStudentsNotice">
    <div>There are currently no activities to display for this class.</div>
    {GetBrandedHideAssessmentPrompt() ?
      null
      :
      <>
        <div>After your students have sufficient practice with {GetBrandedTutorName()}, assign the benchmark assessment <Link className="noActivityLink" to={`/teacher/reports/type/tracking`}>here</Link>.</div>
        <div>
          We recommend students read at least 5-10 stories with {GetBrandedTutorName()} in the default practice mode before taking an assessment.
          For more information, see <a className="noActivityLink" href={educatorSupportHelpLinks.studentPracticeExperience} target="_blank">The Student Practice Experience</a> (password: “ILoveReading”).
        </div>
      </>
    }
  </div>
);

function ActivityCard({ activities, parentOnClick }) {
  const {t} = useTranslation("review_activity")

  let onClick = (activityId, status) => {
    if(status.ready){
      parentOnClick(activityId);
    }
  }

  let getActivityType = (activity) => {
    switch(activity.type){
      case(assignmentTypes.TUTOR):
        return "PRACTICE";
        break;
      case(assignmentTypes.ASSESSMENT):
        return "ASSESSMENT";
        break;
      default:
        return "";
        break;
    }
  }

  let sortedActivities = sortByStatus(activities, true);

  return sortedActivities.map((activity) => {
    let status = getActivityStatus(activity);
    let cardClasses = cx({
      'activityCard': true,
      'clickable': status.ready,
      'error': status.needsAttention,
      'disabled': !status.ready,
      'started': status.started,
    });
    const showScore = activity.scores && status.ready && !status.hideScore
    return(
      <div key={`activity${activity.activityId}`} className={cardClasses} onClick={() => onClick(activity.activityId, status)}>
        <div className="activityDetails">
          <div className="label">{activity && activity.student && activity.student.ui && activity.student.ui.label}</div>
          <div className="date">{new Date(activity.updatedAt || activity.createdAt).toLocaleDateString("en-US")}</div>
          <div className="type">{t(getActivityType(activity))}</div>
        </div>
        <div className="subtext">{status.error}</div>
        <div className="score">
          {showScore ? Math.round(activity.scores.wcpmScore) : " "}
          {showScore && <span className="units">{t("WCPM")}</span>}
          <div className="status">{t(status.label)}</div>
        </div>
      </div>
    );
  });
}

class StudentSearch extends React.PureComponent {
  constructor(props) {
    super(props);

    let cachedShowAssessmentsSelection = getMostRecentShowAssessments();
    if(cachedShowAssessmentsSelection == null){
      cachedShowAssessmentsSelection = true;
    }

    let cachedEquatingSelection = getMostRecentEquating();
    if(cachedEquatingSelection == null) {
      cachedEquatingSelection = true;
    }

    this.state = {
      activities: null,
      showAssessments: !GetBrandedReviewActivityPracticeOnly() && cachedShowAssessmentsSelection,
      equatedScores: cachedEquatingSelection,
    };
  }

  componentDidMount(){
    //Fetch the classrooms to populate the Dropdown
    this.props.initializeRoster();
  }

  componentDidUpdate(prevProps, prevState){
    const rosterStatus = this.props.status ? this.props.status.type : null;
    const prevRosterStatus = prevProps.status ? prevProps.status.type : null;
    const selectedClassId = this.props.selectedClass ? this.props.selectedClass.id : null;
    const prevSelectedClassId = prevProps.selectedClass ? prevProps.selectedClass.id : null;
    const { activities, fetchingActivities, showAssessments, equatedScores } = this.state;

    if(rosterStatus !== prevRosterStatus){
      if(rosterStatus === 'PENDING'){
        this.setState({
          activities: null,
        });
      }else if(rosterStatus === 'COMPLETE' && !activities && !fetchingActivities && selectedClassId !== prevSelectedClassId){
        this.fetchActivities(showAssessments, equatedScores);
      }
    }else{
      if(rosterStatus === 'COMPLETE' && !activities && !fetchingActivities){
        //state contains contains a previously loaded/unchanged complete roster so fetch activities with cached selections
        this.fetchActivities(showAssessments, equatedScores);
      }
    }
  }

  fetchActivities(showAssessments, equatedScores){
    this.setState({
      fetchingActivities: true,
    });
    const { startDate, endDate } = getSchoolYear();

    getValidActivitiesForStudents(
      this.props.students, //list of student ids
      1, // limit one activity  per student
      showAssessments ? [assignmentTypes.ASSESSMENT] : [assignmentTypes.TUTOR], //array of activity types to fetch
      this.state.equatedScores, // whether or not to fetch equated WCPM scores
      null,
      startDate,
      endDate
    ).then((activities) => {
        if(showAssessments == this.state.showAssessments && equatedScores == this.state.equatedScores){
          this.setState({
            activities,
            fetchingActivities: false
          });
        }
      })
      .catch((err) => this.setState({activities: null}));//TODO: Find a better way to do this
  }

  toggleActivityType(){
    let showAssessments = !this.state.showAssessments;
    setMostRecentShowAssessments(showAssessments);
    this.setState({
      showAssessments: showAssessments,
      activities: null
    }, () => this.fetchActivities(this.state.showAssessments, this.state.equatedScores));
  }

  toggleEquating(){
    let equatedScores = !this.state.equatedScores;
    // cache the selection
    setMostRecentEquating(equatedScores);
    this.setState({
      equatedScores,
      activities: null,
    }, () => this.fetchActivities(this.state.showAssessments, this.state.equatedScores));
  }

  handleRosterChange(data, selectionType){
    //if we selected the same school twice in a row, do nothing
    const { selectedSchool, selectedClass } = this.props;
    if((selectionType === 'school' && selectedSchool && selectedSchool.id === data.value)
      || (selectionType === 'classroom' && selectedClass && selectedClass.id === data.value)){
      return;
    }

    //Transform the selection to fit the object model to match the Miller Control.
    //TODO: use the Miller Control in Assignment Selection -OR- move this transform down to the rosterSelect method.
    let rosterData = JSON.parse(JSON.stringify(data));
    rosterData.ui = data;
    rosterData.id = data.value;

    this.props.rosterSelect({
      type: selectionType, //type of selection (e.g. school, class, etc.)
      data: rosterData,
    });
  }

  renderActivities() {
    const { students } = this.props;
    if(!this.state.activities){
      return <Loading />;
    } else if(this.state.activities && this.state.activities.length > 0) {
      return (
        <ActivityCard
          activities={sortActivitiesByDate(this.state.activities)}
          parentOnClick={(id) => this.props.history.push("/teacher/gradebook/scoring/" + id)}
        />
      );
    } else if (!students || (Array.isArray(students) && students.length == 0)) {
      return <ClassWithNoStudents />;
    }else {
      return <NoActivitiesBox />;
    }
  }

  render(){
    // TODO: move some reader styles to paper and move paper to a different file
    // TODO: Notify user when Graphql query fails or when there are no Activities ready to score.
    const { activities } = this.state;
    return (
      <div className="studentSearch">
        <div className="header">
          <div className="studentSearchHeader">{this.props.t("title")}</div>
          <div className="controls">
            {this.props.schools &&
              <span className="selectSchoolContainer">
                <Dropdown
                  placeholder="Select School"
                  value={this.props.selectedSchool}
                  options={this.props.schools.map(school => school.ui)}
                  onChange={(data) => this.handleRosterChange(data, 'school')}
                  type={dropdownTypes.PRIMARY}
                  isLoading={!activities || this.props.schools.length <= 1}
                />
              </span>
            }
            {this.props.classrooms &&
              <Dropdown
                placeholder="Select Classroom"
                value={this.props.selectedClass}
                options={this.props.classrooms.map(classroom => classroom.ui)}
                onChange={(data) => this.handleRosterChange(data, 'classroom')}
                type={dropdownTypes.PRIMARY}
                isLoading={!activities}
              />
            }
            {!GetBrandedReviewActivityPracticeOnly() &&
              <Toggle
                onToggle={this.toggleActivityType.bind(this)}
                leftOptionName={this.props.t("Assessment")}
                rightOptionName={this.props.t("Practice")}
                left={this.state.showAssessments}
                className="activityTypeToggle"
              />
            }
            <Toggle
              onToggle={this.toggleEquating.bind(this)}
              leftOptionName={this.props.t("Adjusted")}
              rightOptionName={this.props.t("Unadjusted")}
              left={this.state.equatedScores}
              className="equatingToggle"
            />
            <TrackingReportButton />
          </div>
        </div>
        <div className="activityContainer">
          {this.renderActivities()}
        </div>
      </div>
    );
  }
}

StudentSearch.defaultProps = {
  schools: [],
  classrooms: [],
  students: [],
  status: '',
}

const mapStateToProps = ({ reports }) => {
  const { roster } = reports;

  return {
    schools: roster.data ? roster.data.school : [],
    status: roster.status,
    classrooms: roster.data ? roster.data.classroom : [],
    students: roster.data ? roster.data.student : [],
    selectedClass: roster.ui ? roster.ui.selections.classroom : null,
    selectedSchool: roster.ui ? roster.ui.selections.school : null,
  };
};

const mapDispatchToProps = (dispatch) => ({
  rosterSelect: (selection) => dispatch(creators.rosterSelect(selection)),
  initializeRoster: () => dispatch(creators.initializeRoster()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation("review_activity")(StudentSearch));
